/**
 * 필터 사이드바 컴포넌트 (데스크톱 전용)
 * 뉴스 스타일 - 왼쪽 고정 레이아웃
 */

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

export interface FilterOption {
  id: string;
  label: string;
  value?: string;
}

export interface FilterSection {
  id: string;
  title: string;
  type: "radio" | "checkbox" | "text" | "custom";
  options: FilterOption[];
  selected?: string | string[];
  onSelect?: (value: string | string[]) => void;
}

interface FilterSidebarProps {
  sections: FilterSection[];
  onReset?: () => void;
  className?: string;
}

export function FilterSidebar({
  sections,
  onReset,
  className,
}: FilterSidebarProps) {
  const handleRadioChange = (sectionId: string, value: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section?.onSelect) {
      section.onSelect(value);
    }
  };

  const handleCheckboxChange = (
    sectionId: string,
    value: string,
    checked: boolean
  ) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section?.onSelect) {
      const current = Array.isArray(section.selected) ? section.selected : [];
      if (checked) {
        section.onSelect([...current, value]);
      } else {
        section.onSelect(current.filter((v) => v !== value));
      }
    }
  };

  return (
    <aside
      className={cn(
        "hidden w-full self-start rounded-xl border border-border bg-card p-4 lg:sticky lg:top-24 lg:block",
        className
      )}
    >
      {/* 필터 헤더 */}
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-sm font-bold text-foreground">필터</h2>
        {onReset && (
          <button
            onClick={onReset}
            className="text-muted-foreground hover:text-[#60a6fa] text-xs transition-colors"
          >
            초기화
          </button>
        )}
      </div>

      {/* 필터 섹션들 */}
      <div className="space-y-5">
        {sections.map((section) => (
          <div key={section.id}>
            <h3 className="mb-2.5 text-xs font-semibold text-foreground">
              {section.title}
            </h3>
            {section.type === "text" && (
              <div className="flex flex-wrap gap-1.5">
                {section.options.map((option) => {
                  const isSelected =
                    section.selected === (option.value || option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() =>
                        handleRadioChange(section.id, option.value || option.id)
                      }
                      className={cn(
                        "min-h-8 rounded-md px-2.5 py-1.5 text-xs transition-colors",
                        isSelected
                          ? "bg-[#60a6fa] text-white"
                          : "bg-muted text-foreground hover:bg-muted/80"
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
            {section.type === "radio" && (
              <RadioGroup
                value={
                  typeof section.selected === "string"
                    ? section.selected
                    : undefined
                }
                onValueChange={(value) => handleRadioChange(section.id, value)}
                className="space-y-2"
              >
                {section.options.map((option) => (
                  <div key={option.id} className="flex min-h-8 items-center space-x-2">
                    <RadioGroupItem
                      value={option.value || option.id}
                      id={`${section.id}-${option.id}`}
                      className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                    />
                    <Label
                      htmlFor={`${section.id}-${option.id}`}
                      className="text-foreground text-xs font-normal cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
            {section.type === "checkbox" && (
              <div className="space-y-1.5">
                {section.options.map((option) => {
                  const selected = Array.isArray(section.selected)
                    ? section.selected
                    : [];
                  const isChecked = selected.includes(
                    option.value || option.id
                  );
                  return (
                    <div
                      key={option.id}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`${section.id}-${option.id}`}
                        checked={isChecked}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(
                            section.id,
                            option.value || option.id,
                            checked === true
                          )
                        }
                        className="border-foreground data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                      />
                      <Label
                        htmlFor={`${section.id}-${option.id}`}
                        className="text-foreground text-xs font-normal cursor-pointer"
                      >
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}
